/**
 * Agents API client — feature-scoped types and fetchers.
 *
 * Subagent C owns the thin `AgentSummary` in `@/api/types`. This file
 * lives next to the feature pages and carries the richer shapes needed
 * by the detail view, config form, and wizard. Where the two overlap,
 * we re-export the base type so consumers can pick whichever they need.
 */
import { api } from '@/api/client';
import type {
  AgentListResponse as BaseListResponse,
  AgentStatus as BaseStatus,
  EffortLevel as BaseEffortLevel,
} from '@/api/types';

export type AgentStatus = BaseStatus; // active | inactive | failed | unknown
export type PermissionMode = 'bypassPermissions' | 'acceptEdits' | 'plan' | 'default';
export type DmPolicy = 'allowlist' | 'pairing' | 'disabled';
export type EffortLevel = BaseEffortLevel; // low | medium | high | max

export interface DiscordChannelConfig {
  enabled: boolean;
  bot_token_masked?: string;
  user_id_allowlist: string[];
  channel_ids: string[];
  dm_policy: DmPolicy;
  bot_name?: string;
  bot_avatar_url?: string;
}

export interface AgentSystemdConfig {
  auto_restart: boolean;
  restart_sec: number;
}

export interface AgentConfig {
  name: string;
  description: string;
  enabled: boolean;
  permission_mode: PermissionMode;
  effort: EffortLevel;
  disable_adaptive_thinking: boolean;
  systemd: AgentSystemdConfig;
  discord?: DiscordChannelConfig;
  // HEAD sha used for If-Match on PATCH (the backend returns it inline;
  // we keep it on the object so the form doesn't need a second fetch).
  version?: string;
}

export interface AgentRuntimeStatus {
  name: string;
  status: AgentStatus;
  pid?: number;
  uptime_seconds?: number;
  memory_bytes?: number;
  last_error?: string;
  last_error_at?: string;
  last_message_at?: string;
  messages_today: number;
  messages_week: number;
  messages_total: number;
  session_file?: string;
  session_file_bytes?: number;
  discord_bot_name?: string;
}

export interface JobResponse {
  job_id: string;
}

export interface CreateAgentPayload {
  name: string;
  description: string;
  permission_mode: PermissionMode;
  effort?: EffortLevel;
  disable_adaptive_thinking?: boolean;
  systemd: AgentSystemdConfig;
  discord?: {
    bot_token: string;
    user_id_allowlist: string[];
    channel_ids: string[];
    dm_policy: DmPolicy;
  };
  claude_md?: string;
}

export interface UpdateAgentPayload {
  description?: string;
  enabled?: boolean;
  permission_mode?: PermissionMode;
  effort?: EffortLevel;
  disable_adaptive_thinking?: boolean;
  systemd?: Partial<AgentSystemdConfig>;
  discord?: {
    bot_token?: string;
    user_id_allowlist?: string[];
    channel_ids?: string[];
    dm_policy?: DmPolicy;
  };
}

export type AgentListResponse = BaseListResponse;

// ---------- Backend v2 raw shapes ----------
// These mirror panel/backend/backend/models/agent.py. The translation
// functions below map them into the v1-style AgentConfig/RuntimeStatus
// shapes the feature pages still expect, so we can flip the frontend
// without touching every detail page.

interface V2AgentChannelOut {
  kind: 'discord' | 'telegram';
  enabled: boolean;
  bot_token_masked: string;
  user_ids: string[];
  channel_ids: string[];
  dm_policy: string;
}

interface V2AgentOut {
  id: number;
  name: string;
  description: string;
  enabled: boolean;
  permissions_mode: 'bypassPermissions' | 'acceptEdits' | 'plan' | 'default';
  effort: 'low' | 'medium' | 'high' | 'max';
  disable_adaptive_thinking: boolean;
  restart_sec: number;
  created_at: string;
  updated_at: string;
  last_deployed_at?: string | null;
  channels: V2AgentChannelOut[];
  etag?: string;
}

interface V2AgentStatusOut {
  name: string;
  systemd_state: AgentStatus;
  uptime_seconds: number | null;
  main_pid: number | null;
  memory_bytes: number | null;
  discord_connected: boolean;
  last_error: string | null;
}

function v2ToAgentConfig(raw: V2AgentOut): AgentConfig {
  const discordCh = raw.channels?.find((c) => c.kind === 'discord');
  return {
    name: raw.name,
    description: raw.description ?? '',
    enabled: raw.enabled,
    permission_mode: (raw.permissions_mode === 'acceptEdits'
      ? 'default'
      : raw.permissions_mode) as PermissionMode,
    effort: (raw.effort ?? 'medium') as EffortLevel,
    disable_adaptive_thinking: raw.disable_adaptive_thinking ?? false,
    systemd: {
      auto_restart: true,
      restart_sec: raw.restart_sec,
    },
    discord: discordCh
      ? {
          enabled: discordCh.enabled,
          bot_token_masked: discordCh.bot_token_masked,
          user_id_allowlist: discordCh.user_ids ?? [],
          channel_ids: discordCh.channel_ids ?? [],
          dm_policy: (discordCh.dm_policy as DmPolicy) ?? 'allowlist',
        }
      : undefined,
    version: raw.etag,
  };
}

function v2ToRuntimeStatus(raw: V2AgentStatusOut): AgentRuntimeStatus {
  return {
    name: raw.name,
    status: raw.systemd_state,
    pid: raw.main_pid ?? undefined,
    uptime_seconds: raw.uptime_seconds ?? undefined,
    memory_bytes: raw.memory_bytes ?? undefined,
    last_error: raw.last_error ?? undefined,
    messages_today: 0,
    messages_week: 0,
    messages_total: 0,
  };
}

function v1PayloadToV2(payload: UpdateAgentPayload): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (payload.description !== undefined) out.description = payload.description;
  if (payload.enabled !== undefined) out.enabled = payload.enabled;
  if (payload.permission_mode !== undefined) out.permissions_mode = payload.permission_mode;
  if (payload.effort !== undefined) out.effort = payload.effort;
  if (payload.disable_adaptive_thinking !== undefined)
    out.disable_adaptive_thinking = payload.disable_adaptive_thinking;
  if (payload.systemd?.restart_sec !== undefined) out.restart_sec = payload.systemd.restart_sec;
  if (payload.discord !== undefined) {
    const d = payload.discord;
    out.discord = {
      enabled: true,
      ...(d.bot_token !== undefined ? { bot_token: d.bot_token } : {}),
      ...(d.user_id_allowlist !== undefined ? { user_ids: d.user_id_allowlist } : {}),
      ...(d.channel_ids !== undefined ? { channel_ids: d.channel_ids } : {}),
      ...(d.dm_policy !== undefined ? { dm_policy: d.dm_policy } : {}),
    };
  }
  return out;
}

export const agentsApi = {
  async list(params?: { filter?: string; cursor?: string; limit?: number }): Promise<AgentListResponse> {
    const search = new URLSearchParams();
    if (params?.filter && params.filter !== 'all') search.set('filter', params.filter);
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return api<AgentListResponse>(`/agents${qs ? `?${qs}` : ''}`);
  },

  async get(name: string): Promise<AgentConfig> {
    const raw = await api<V2AgentOut>(`/agents/${encodeURIComponent(name)}`);
    return v2ToAgentConfig(raw);
  },

  async status(name: string): Promise<AgentRuntimeStatus> {
    const raw = await api<V2AgentStatusOut>(`/agents/${encodeURIComponent(name)}/status`);
    return v2ToRuntimeStatus(raw);
  },

  async create(payload: CreateAgentPayload): Promise<V2AgentOut> {
    const body: Record<string, unknown> = {
      name: payload.name,
      description: payload.description,
      permissions_mode: payload.permission_mode,
      effort: payload.effort ?? 'medium',
      disable_adaptive_thinking: payload.disable_adaptive_thinking ?? false,
      restart_sec: payload.systemd?.restart_sec ?? 15,
    };
    if (payload.discord) {
      body.discord = {
        enabled: true,
        bot_token: payload.discord.bot_token,
        user_ids: payload.discord.user_id_allowlist,
        channel_ids: payload.discord.channel_ids,
        dm_policy: payload.discord.dm_policy,
      };
    }
    return api<V2AgentOut>('/agents', { method: 'POST', body });
  },

  async update(name: string, version: string, payload: UpdateAgentPayload): Promise<V2AgentOut> {
    const body = v1PayloadToV2(payload);
    return api<V2AgentOut>(`/agents/${encodeURIComponent(name)}`, {
      method: 'PATCH',
      body,
      ifMatch: version,
    });
  },

  async remove(name: string, _stepupToken?: string): Promise<void> {
    await api<void>(`/agents/${encodeURIComponent(name)}`, {
      method: 'DELETE',
      parse: false,
    });
  },

  async action(name: string, action: 'start' | 'stop' | 'restart'): Promise<{ ok: boolean }> {
    return api<{ ok: boolean }>(`/agents/${encodeURIComponent(name)}/${action}`, {
      method: 'POST',
    });
  },
};
